import { type Note } from '@eigtech/notes-types'
import { DOMParser } from '@xmldom/xmldom'
import { useMemo } from 'react'
import { formatMessage } from '../utils/formatMessage'
import { getMessageData } from '../utils/getMessageData'
import { hasHTML } from '../utils/hasHtml'
import { isJSON } from '../utils/isJson'

export function useNormalizedNoteText(note: Note) {
  return useMemo(() => {
    const messageData = getMessageData(note)
    let normalizedMessageData = messageData

    if (hasHTML(messageData)) {
      // @xmldom/xmldom is an XML parser — pre-process HTML into valid XML first:
      // self-close void elements (skip already-self-closed ones via negative lookbehind),
      // replace &nbsp; (not a built-in XML entity), then wrap in a single root element.
      const voidTags = 'area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr'
      const xmlContent = `<body>${messageData
        .replace(
          new RegExp(`<(${voidTags})(\\s[^>]*)?(?<!\\/)>`, 'gi'),
          (_, tag, attrs = '') => `<${tag}${attrs}/>`
        )
        .replace(/&nbsp;/g, ' ')}</body>`

      normalizedMessageData =
        new DOMParser({ onError: () => {} })
          .parseFromString(xmlContent, 'text/xml')
          .documentElement?.textContent // replace 3 or more spaces in a row with one space
          ?.replace(/ {3,}/g, ' ')
          // replace 3 or more line breaks in a row with one line break
          ?.replace(/\n{3,}/g, '\n') ??
        // fallback to original message data if parsing fails
        messageData
    } else if (isJSON(messageData)) {
      normalizedMessageData = JSON.stringify(messageData, null, 4)
    } else {
      normalizedMessageData = formatMessage(messageData)
    }

    return normalizedMessageData
  }, [note])
}
